import { useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import type { Customer } from "@/types";

interface CustomerPickerModalProps {
  open: boolean;
  onClose: () => void;
  customers: Customer[];
  selectedId: string | null;
  onSelect: (customer: Customer | null) => void;
}

export default function CustomerPickerModal({
  open,
  onClose,
  customers,
  selectedId,
  onSelect,
}: CustomerPickerModalProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const list = customers.filter((c) => c.active);
    if (!term) return list.slice(0, 60);
    return list
      .filter((c) => {
        const name = `${c.first_name} ${c.last_name}`.toLowerCase();
        return (
          name.includes(term) ||
          (c.document ?? "").toLowerCase().includes(term) ||
          (c.phone ?? "").toLowerCase().includes(term) ||
          (c.whatsapp ?? "").toLowerCase().includes(term) ||
          (c.email ?? "").toLowerCase().includes(term)
        );
      })
      .slice(0, 60);
  }, [customers, query]);

  const choose = (customer: Customer | null) => {
    onSelect(customer);
    setQuery("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Seleccionar cliente"
      subtitle="Los clientes mayoristas aplican precio mayorista automáticamente."
      size="md"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
          <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por nombre, documento, teléfono o correo"
            className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-300"
          />
        </div>

        <button
          type="button"
          onClick={() => choose(null)}
          className={`flex items-center gap-3 rounded-md border px-4 py-3 text-left transition-colors ${
            selectedId === null
              ? "border-primary-300 bg-primary-50"
              : "border-background-200 bg-background-50 hover:bg-background-100"
          }`}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-foreground-600">
            <i className="ri-user-line text-lg"></i>
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground-900">Consumidor final</span>
            <span className="block text-xs text-foreground-500">Venta sin cliente registrado</span>
          </span>
        </button>

        <div className="flex max-h-[46vh] flex-col gap-2 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="rounded-md border border-dashed border-background-300 px-4 py-8 text-center text-sm text-foreground-500">
              No se encontraron clientes.
            </div>
          ) : (
            filtered.map((customer) => {
              const isWholesale = customer.customer_type === "mayorista";
              return (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => choose(customer)}
                  className={`flex items-center gap-3 rounded-md border px-4 py-3 text-left transition-colors ${
                    selectedId === customer.id
                      ? "border-primary-300 bg-primary-50"
                      : "border-background-200 bg-background-50 hover:bg-background-100"
                  }`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                    {(customer.first_name?.[0] ?? "C").toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-foreground-900">
                        {customer.first_name} {customer.last_name}
                      </span>
                      {isWholesale ? (
                        <span className="shrink-0 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-semibold text-accent-800">
                          Mayorista
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-foreground-500">
                      {[customer.document, customer.phone || customer.whatsapp]
                        .filter(Boolean)
                        .join(" · ") || "Sin datos de contacto"}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-lg text-foreground-400"></i>
                </button>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}